import { Loader2, RotateCcw } from "lucide-react"
import { cn } from "@/lib/utils"

type WidgetStatus = "pending" | "accepted" | "rejected"

export interface FileEditWidgetProps {
  /** Internal storage path (e.g. ws/{orgId}/{slug}.md) - never render directly; use displayPath. */
  fileName: string
  /** Human-facing "{workspaceSlug}/{docSlug}" path from the backend, when available. */
  displayPath?: string | null
  /** Full shareable webapp URL, when available. */
  url?: string | null
  /** Document title for line 1 when available. */
  title?: string | null
  additions: number
  deletions: number
  status: WidgetStatus
  changeStatus?: string | null
  requiresAcceptance?: boolean
  onOpen?: (documentId?: string) => void | Promise<void>
  onReview?: () => void
  onAccept?: () => void
  onReject?: () => void
  onUndoAccepted?: () => void
  isProcessing?: boolean
}

function pathLabel(displayPath: string | null | undefined, fileName: string): string {
  if (displayPath) return displayPath
  return fileName.split("/").pop() || fileName
}

function titleLabel(title: string | null | undefined, displayPath: string | null | undefined, fileName: string): string {
  if (title?.trim()) return title.trim()
  const path = displayPath || fileName
  const base = path.split("/").pop()
  return base || fileName
}

export function FileEditWidget({
  fileName,
  displayPath,
  url,
  title,
  additions,
  deletions,
  status,
  requiresAcceptance = true,
  onOpen,
  onReview,
  onAccept,
  onReject,
  onUndoAccepted,
  isProcessing = false,
}: FileEditWidgetProps) {
  const actionable = requiresAcceptance && status === "pending"
  const line1Title = titleLabel(title, displayPath, fileName)
  const linesChanged = additions + deletions
  const linesLabel = linesChanged === 1 ? "1 line changed" : `${linesChanged} lines changed`

  const openDoc = () => {
    if (onOpen) void onOpen()
  }

  return (
    <div className="py-1.5" data-testid="file-edit-widget">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm min-w-0">
        <button
          type="button"
          onClick={openDoc}
          disabled={!onOpen}
          className={cn(
            "font-medium text-foreground/90 truncate max-w-full text-left",
            onOpen && "hover:underline cursor-pointer",
            !onOpen && "cursor-default",
          )}
          title={url || pathLabel(displayPath, fileName)}
        >
          {line1Title}
        </button>
        {status === "rejected" ? (
          <span className="text-xs text-muted-foreground shrink-0">Rejected</span>
        ) : status === "accepted" ? (
          <span className="text-xs text-muted-foreground shrink-0">{linesLabel}</span>
        ) : (
          <span className="text-xs text-muted-foreground shrink-0">
            <span className="text-green-600 dark:text-green-400 font-medium">+{additions}</span>
            {" / "}
            <span className="text-red-600 dark:text-red-400 font-medium">-{deletions}</span>
          </span>
        )}
        {actionable && (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs shrink-0">
            {onAccept && (
              <button
                type="button"
                onClick={onAccept}
                disabled={isProcessing}
                className="text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50"
              >
                {isProcessing ? <Loader2 className="h-3 w-3 animate-spin inline" /> : "Keep"}
              </button>
            )}
            {onReject && (
              <button
                type="button"
                onClick={onReject}
                disabled={isProcessing}
                className="text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50"
              >
                Undo
              </button>
            )}
            {onReview && (
              <button
                type="button"
                onClick={onReview}
                disabled={isProcessing}
                className="text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50"
              >
                Diff
              </button>
            )}
          </span>
        )}
        {status === "accepted" && onUndoAccepted && (
          <button
            type="button"
            onClick={onUndoAccepted}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            <RotateCcw className="h-3 w-3" />
            Undo
          </button>
        )}
      </div>
    </div>
  )
}
